import React, { useEffect, useRef, useState } from "react";
import {
  BarChart as BarChartIcon,
  ShowChart as LineChartIcon,
  Timeline as AreaChartIcon,
  Search as SearchIcon,
  CloseRounded as CloseRoundedIcon,
  MoreHoriz as MoreHorizIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Select,
  Skeleton,
  TextField,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useDashboardStore } from "../stores/dashboard.store";
import { useOrganizationStore } from "../stores/organization.store";
import { DashboardStatistics, StatisticType } from "../types/dashboard.type";
import {
  OrganizationType,
  isSiteOperationsOrgType,
} from "../types/organization.type";
import { useTranslation } from "react-i18next";
import { SetupCoachmark } from "../components/selfSetup/SetupCoachmark";
import { useSelfSetupTour } from "../hooks/useSelfSetupTour";
import { HELP_GUIDE_CUSTOMIZE, HELP_GUIDE_KPI_LIMITS } from "../constants/knowledgeBaseTryIt";
import {
  clearHelpGuideActive,
  markHelpGuideActive,
} from "../utils/helpGuideSession";
import { helpI18n } from "../i18n/help";
import KPILimitsDialog from "../components/dashboard/KPILimitsDialog";
import { isTrackableKpi } from "../constants/kpiTargets";
import { format } from "date-fns";
import { useNavigate, useLocation } from "react-router-dom";
import { useKPIStore } from "../stores/kpi.store";
import {
  getStatsForOrgType,
  getDefaultStatsForOrgType,
  isStatAllowedForOrgType,
  getStatTitleKey,
  getStatValueKind,
  humanizeStatKey,
  migrateLegacyStatKeys,
  getDashboardExtrasForOrgTab,
  DASHBOARD_EXTRA_KPI,
  DASHBOARD_EXTRA_MAP,
  type DashboardExtraWidgetId,
} from "../constants/dashboardStats.catalog";
import DashboardCustomizeWidgetList from "../components/dashboard/DashboardCustomizeWidgetList";
import {
  sanitizePickableIds,
  ZERO_MONTHLY_EXTRAS_KEY,
  ZERO_OVERVIEW_EXTRAS_KEY,
  ZERO_OVERVIEW_STATS_KEY,
} from "../utils/dashboardCustomizePreferences";
import { useAuthStore } from "../stores/auth.store";
import { useAuthPermissions } from "../hooks/useAuthPermissions";
import { orgScopedPermission } from "../auth/jwt";
import { CONTENT_SHAPE } from "../constants/contentColors";
import { ZERO_ICONS } from "../constants/zeroIcons";
import { useNumberFormat } from "../hooks/useNumberFormat";
import { operationalStatPolarity } from "../utils/metricPolarity";
import { formatMonth } from "../utils/numberFormat";
import { contentBorders } from "../theme/createContentTheme";
import PageHeader from "../components/PageHeader";
import SectionHeader from "../components/SectionHeader";
import EmptyState from "../components/EmptyState";
import KpiFigure, { type KpiDelta } from "../components/metrics/KpiFigure";
import MetricStrip from "../components/metrics/MetricStrip";
import FreshnessLabel from "../components/operations/FreshnessLabel";
import CollectionFlow, { type FlowStage } from "../components/operations/CollectionFlow";
import MaterialList from "../components/operations/MaterialList";
import DailyRequestsChart, { type DailyChartType } from "../components/operations/DailyRequestsChart";
import DailyMaterialSplit from "../components/operations/DailyMaterialSplit";
import ContainerHealth from "../components/operations/ContainerHealth";
import KpiTargetsTable from "../components/operations/KpiTargetsTable";
import OrderStatusDonut, { type OrderStatusSlice } from "../components/operations/OrderStatusDonut";
import SiteCommandSurface from "../components/operations/SiteCommandSurface";
import FleetCommandSurface from "../components/operations/FleetCommandSurface";
import { STAT_ICONS } from "../components/operations/statIcons";
import PilotDashboard from "./PilotDashboard";

/** Monthly values are `yyyy-MM-01T00:00:00Z` built from the local calendar. */
function monthStartOf(value: string): Date {
  const match = /^(\d{4})-(\d{2})/.exec(value);
  const now = new Date();
  return match
    ? new Date(Number(match[1]), Number(match[2]) - 1, 1)
    : new Date(now.getFullYear(), now.getMonth(), 1);
}

const StandardDashboard: React.FC = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const { t: tHelp } = useTranslation("translation", { i18n: helpI18n });
  const { selectedOrganization } = useOrganizationStore();
  const {
    statistics,
    aggregatedStatistics,
    selectedStats,
    isLoading,
    isLoadingAggregated,
    error,
    aggregatedError,
    selectedMonth,
    fetchDashboardStatistics,
    fetchAggregatedDashboardStatistics,
    clearAggregatedDashboard,
    setSelectedStats,
    setSelectedMonth,
    loadDashboardPreferences,
    statisticsFetchedAt,
    aggregatedFetchedAt,
  } = useDashboardStore();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isStep,
    completeStep,
    dismissTour,
    tourPhase,
    setTourPhase,
  } = useSelfSetupTour();
  const showDashboardTour = isStep("dashboard");
  const [helpCustomizeGuide, setHelpCustomizeGuide] = useState(false);
  const [helpKpiGuide, setHelpKpiGuide] = useState(false);
  const [kpiTourPhase, setKpiTourPhase] = useState<"button" | "form">("button");
  /** Knowledge Base customize guide local phase (self-setup uses tourPhase). */
  const [helpCustomizePhase, setHelpCustomizePhase] = useState<
    "button" | "dialog"
  >("button");
  type CustomizeDialogPhase = "search" | "metrics" | "save";
  const [customizeDialogPhase, setCustomizeDialogPhase] =
    useState<CustomizeDialogPhase>("search");

  const { limits } = useKPIStore();
  const hasEnabledKPIs = limits.some((limit) => limit.isEnabled);
  const hasTrackedKPIs = limits.some((limit) => limit.isEnabled && isTrackableKpi(limit.name));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedStat, setSelectedStat] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openKPIDialog, setOpenKPIDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [chartTypes, setChartTypes] = useState<
    Record<string, "bar" | "line" | "area" | "pie">
  >({});
  const [dashboardViewTab, setDashboardViewTab] = useState(0);
  const [dailyView, setDailyView] = useState<"total" | "material">("total");
  const [selectedOverviewStats, setSelectedOverviewStats] = useState<
    StatisticType[]
  >([]);
  const [monthlyExtras, setMonthlyExtras] = useState<DashboardExtraWidgetId[]>(
    [],
  );
  const [overviewExtras, setOverviewExtras] = useState<DashboardExtraWidgetId[]>(
    [],
  );

  // Adding a KPI limit shows the KPI section again, even if it was turned off
  // in Customize — otherwise the new limit has nowhere to appear.
  const enabledKpiCount = limits.filter((limit) => limit.isEnabled).length;
  const previousKpiCount = useRef(enabledKpiCount);
  useEffect(() => {
    if (enabledKpiCount > previousKpiCount.current) {
      const withKpi = (current: DashboardExtraWidgetId[], storageKey: string): DashboardExtraWidgetId[] => {
        if (current.includes(DASHBOARD_EXTRA_KPI)) return current;
        const next: DashboardExtraWidgetId[] = [...current, DASHBOARD_EXTRA_KPI];
        try {
          sessionStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          /* storage unavailable: the section still shows for this visit */
        }
        return next;
      };
      setMonthlyExtras((current) => withKpi(current, ZERO_MONTHLY_EXTRAS_KEY));
      setOverviewExtras((current) => withKpi(current, ZERO_OVERVIEW_EXTRAS_KEY));
    }
    previousKpiCount.current = enabledKpiCount;
  }, [enabledKpiCount]);

  /** Self-setup on-page tip or Knowledge Base customize guide. */
  const activeCustomizeGuide =
    (showDashboardTour &&
      (tourPhase === "customizeButton" ||
        tourPhase === "dialogSearch" ||
        tourPhase === "dialogMetrics" ||
        tourPhase === "dialogSave")) ||
    helpCustomizeGuide;

  const showCustomizeButtonTip =
    (showDashboardTour && tourPhase === "customizeButton") ||
    (helpCustomizeGuide && helpCustomizePhase === "button");

  const selfSetupDialogPhase: CustomizeDialogPhase | null =
    tourPhase === "dialogSearch"
      ? "search"
      : tourPhase === "dialogMetrics"
        ? "metrics"
        : tourPhase === "dialogSave"
          ? "save"
          : null;

  const showCustomizeDialogTip =
    (showDashboardTour && selfSetupDialogPhase != null) ||
    (helpCustomizeGuide && helpCustomizePhase === "dialog");

  const activeCustomizeDialogPhase: CustomizeDialogPhase =
    selfSetupDialogPhase ?? customizeDialogPhase;

  const finishKpiGuide = () => {
    setHelpKpiGuide(false);
    setKpiTourPhase("button");
    setOpenKPIDialog(false);
    clearHelpGuideActive();
  };

  /** Close tip without completing the dashboard step (Later / X). */
  const softDismissDashboardTour = () => {
    setOpenDialog(false);
    setOpenKPIDialog(false);
    if (helpCustomizeGuide) {
      setHelpCustomizeGuide(false);
      setHelpCustomizePhase("button");
      setCustomizeDialogPhase("search");
      clearHelpGuideActive();
      return;
    }
    // Keep current tourPhase so dismissTour saves the exact tip (incl. dialog*).
    dismissTour();
  };

  const advanceCustomizeDialogTour = () => {
    if (showDashboardTour && selfSetupDialogPhase) {
      if (tourPhase === "dialogSearch") {
        setTourPhase("dialogMetrics");
        return;
      }
      if (tourPhase === "dialogMetrics") {
        setTourPhase("dialogSave");
        return;
      }
      leaveCustomizeToKeyNumbers();
      return;
    }
    if (customizeDialogPhase === "search") {
      setCustomizeDialogPhase("metrics");
      return;
    }
    if (customizeDialogPhase === "metrics") {
      setCustomizeDialogPhase("save");
      return;
    }
    leaveCustomizeToKeyNumbers();
  };

  /** After customize tips: continue to Key Numbers (do not complete dashboard yet). */
  const leaveCustomizeToKeyNumbers = () => {
    setOpenDialog(false);
    setCustomizeDialogPhase("search");
    setHelpCustomizePhase("button");
    if (helpCustomizeGuide) {
      setHelpCustomizeGuide(false);
      clearHelpGuideActive();
      return;
    }
    setTourPhase("keyNumbers");
  };

  const goNextDashboardTip = () => {
    if (!showDashboardTour) return;
    if (tourPhase === "monthlyTab") {
      setTourPhase("headerActions");
      return;
    }
    if (tourPhase === "headerActions") {
      setOpenKPIDialog(true);
      setTourPhase("kpiForm");
      return;
    }
    if (tourPhase === "kpiForm") {
      setTourPhase("kpiList");
      return;
    }
    if (tourPhase === "kpiList") {
      setOpenKPIDialog(false);
      setDashboardViewTab(0);
      setTourPhase("monthSelect");
      return;
    }
    if (tourPhase === "monthSelect") {
      setTourPhase("customizeButton");
      return;
    }
    if (tourPhase === "keyNumbers") {
      setTourPhase("charts");
      return;
    }
    if (tourPhase === "charts") {
      setOpenDialog(false);
      setOpenKPIDialog(false);
      completeStep("dashboard");
    }
  };

  // Keep MONTHLY tab selected while guiding month-related tips.
  useEffect(() => {
    if (
      !showDashboardTour ||
      (tourPhase !== "monthlyTab" &&
        tourPhase !== "headerActions" &&
        tourPhase !== "kpiForm" &&
        tourPhase !== "kpiList" &&
        tourPhase !== "monthSelect")
    ) {
      return;
    }
    setDashboardViewTab(0);
  }, [showDashboardTour, tourPhase]);

  // Keep KPI / Customize dialogs open while their self-setup tips are active (incl. resume).
  useEffect(() => {
    if (!showDashboardTour) return;
    if (tourPhase === "kpiForm" || tourPhase === "kpiList") {
      setOpenKPIDialog(true);
    }
    if (
      tourPhase === "dialogSearch" ||
      tourPhase === "dialogMetrics" ||
      tourPhase === "dialogSave"
    ) {
      setOpenDialog(true);
    }
  }, [showDashboardTour, tourPhase]);

  // Only reset help-customize local phase when that guide ends.
  const wasHelpCustomizeRef = useRef(false);
  useEffect(() => {
    if (!helpCustomizeGuide) {
      if (wasHelpCustomizeRef.current) {
        setHelpCustomizePhase("button");
        setCustomizeDialogPhase("search");
      }
      wasHelpCustomizeRef.current = false;
      return;
    }
    if (!wasHelpCustomizeRef.current) {
      setHelpCustomizePhase("button");
    }
    wasHelpCustomizeRef.current = true;
  }, [helpCustomizeGuide]);

  useEffect(() => {
    if (!showCustomizeButtonTip) return;
    window.requestAnimationFrame(() => {
      document
        .getElementById("self-setup-dashboard-customize")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [showCustomizeButtonTip]);

  useEffect(() => {
    if (!showCustomizeDialogTip || !openDialog) return;
    const phase = activeCustomizeDialogPhase;
    const id =
      phase === "search"
        ? "help-customize-search"
        : phase === "metrics"
          ? "help-customize-metrics"
          : "help-customize-save";
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }, [showCustomizeDialogTip, activeCustomizeDialogPhase, openDialog]);

  useEffect(() => {
    if (!showDashboardTour) return;
    const idByPhase: Partial<Record<string, string>> = {
      monthlyTab: "self-setup-dashboard-monthly-tab",
      headerActions: "help-kpi-limits-button",
      kpiForm: "self-setup-kpi-form",
      kpiList: "self-setup-kpi-list",
      monthSelect: "self-setup-dashboard-month-select",
      keyNumbers: "self-setup-dashboard-key-numbers",
      charts: "self-setup-dashboard-charts",
      customizeButton: "self-setup-dashboard-customize",
    };
    const id = idByPhase[tourPhase];
    if (!id) return;
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }, [showDashboardTour, tourPhase]);

  // Skip empty Key Numbers / Charts sections during the guided tour.
  useEffect(() => {
    if (!showDashboardTour) return;
    if (tourPhase !== "keyNumbers" && tourPhase !== "charts") return;
    if (dashboardViewTab === 0 && isLoading) return;
    if (dashboardViewTab === 1 && isLoadingAggregated) return;

    const frame = window.requestAnimationFrame(() => {
      if (tourPhase === "keyNumbers") {
        if (!document.getElementById("self-setup-dashboard-key-numbers")) {
          if (document.getElementById("self-setup-dashboard-charts")) {
            setTourPhase("charts");
          } else {
            completeStep("dashboard");
          }
        }
        return;
      }
      if (
        tourPhase === "charts" &&
        !document.getElementById("self-setup-dashboard-charts")
      ) {
        completeStep("dashboard");
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [
    showDashboardTour,
    tourPhase,
    dashboardViewTab,
    isLoading,
    isLoadingAggregated,
    statistics,
    aggregatedStatistics,
    selectedStats,
    setTourPhase,
    completeStep,
  ]);

  useEffect(() => {
    if (!helpKpiGuide || kpiTourPhase !== "button") return;
    window.requestAnimationFrame(() => {
      document
        .getElementById("help-kpi-limits-button")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [helpKpiGuide, kpiTourPhase]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const setup = params.get("selfSetup");
    const helpGuide = params.get("helpGuide");
    if (!setup && !helpGuide) return;

    if (setup === "kpi") {
      setOpenKPIDialog(true);
    }
    if (helpGuide === HELP_GUIDE_KPI_LIMITS) {
      markHelpGuideActive(helpGuide);
      setHelpKpiGuide(true);
      setKpiTourPhase("button");
    }
    const isCustomize =
      setup === "customize" || helpGuide === HELP_GUIDE_CUSTOMIZE;
    if (isCustomize) {
      if (helpGuide === HELP_GUIDE_CUSTOMIZE) {
        markHelpGuideActive(helpGuide);
        setHelpCustomizeGuide(true);
        setHelpCustomizePhase("button");
      }
    }

    // Only strip query when Dashboard consumed it — leave language/appearance for Layout.
    if (
      setup ||
      helpGuide === HELP_GUIDE_CUSTOMIZE ||
      helpGuide === HELP_GUIDE_KPI_LIMITS
    ) {
      navigate({ pathname: location.pathname, search: "" }, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  const handleOpenDialog = () => {
    setOpenDialog(true);
    if (showDashboardTour && tourPhase === "customizeButton") {
      setTourPhase("dialogSearch");
    } else if (helpCustomizeGuide) {
      setHelpCustomizePhase("dialog");
      setCustomizeDialogPhase("search");
    }
  };


  const handleCloseDialog = () => {
    setOpenDialog(false);
    if (
      showDashboardTour &&
      (tourPhase === "dialogSearch" ||
        tourPhase === "dialogMetrics" ||
        tourPhase === "dialogSave")
    ) {
      setTourPhase("customizeButton");
    } else if (helpCustomizeGuide && helpCustomizePhase === "dialog") {
      setHelpCustomizePhase("button");
      setCustomizeDialogPhase("search");
    }
  };

  const getDisplayTitle = (statKey: string): string => {
    return t(getStatTitleKey(statKey, selectedOrganization?.organization_type), {
      defaultValue: humanizeStatKey(statKey),
    });
  };

  const [availableMonths] = useState(() => {
    const months: { value: string; date: Date }[] = [];
    const currentDate = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - i,
        1
      );
      months.push({
        value: format(date, "yyyy-MM-dd'T'HH:mm:ss'Z'"),
        date,
      });
    }
    return months;
  });

  useEffect(() => {
    loadDashboardPreferences();

    const orgType = selectedOrganization?.organization_type;
    if (!orgType) return;

    const { selectedMonth: storedMonth, selectedStats: storedStats } =
      useDashboardStore.getState();

    const currentDate = new Date();
    const defaultMonth = format(
      new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
      "yyyy-MM-dd'T'HH:mm:ss'Z'"
    );

    const monthToUse = storedMonth || defaultMonth;
    if (monthToUse !== storedMonth) {
      setSelectedMonth(monthToUse);
    }

    // Keep only stats relevant to this org type; otherwise fall back to type defaults.
    const allowedStored = migrateLegacyStatKeys(storedStats ?? []).filter((stat) =>
      isStatAllowedForOrgType(stat, orgType)
    );
    const monthlyStats =
      allowedStored.length > 0
        ? allowedStored
        : getDefaultStatsForOrgType(orgType);
    setSelectedStats(monthlyStats);

    const monthlyExtraIds = getDashboardExtrasForOrgTab(orgType, "monthly");
    const overviewExtraIds = getDashboardExtrasForOrgTab(orgType, "overview");
    try {
      const storedMonthlyExtras = sessionStorage.getItem(ZERO_MONTHLY_EXTRAS_KEY);
      setMonthlyExtras(
        sanitizePickableIds(
          storedMonthlyExtras ? JSON.parse(storedMonthlyExtras) : undefined,
          monthlyExtraIds,
        ),
      );
    } catch {
      setMonthlyExtras([...monthlyExtraIds]);
    }
    try {
      const storedOverviewExtras = sessionStorage.getItem(ZERO_OVERVIEW_EXTRAS_KEY);
      setOverviewExtras(
        sanitizePickableIds(
          storedOverviewExtras ? JSON.parse(storedOverviewExtras) : undefined,
          overviewExtraIds,
        ),
      );
    } catch {
      setOverviewExtras([...overviewExtraIds]);
    }

    const allowedOverview = (ids: StatisticType[]) =>
      ids.filter((stat) => isStatAllowedForOrgType(stat, orgType));
    try {
      const storedOverview = sessionStorage.getItem(ZERO_OVERVIEW_STATS_KEY);
      if (storedOverview) {
        const parsed = JSON.parse(storedOverview) as StatisticType[];
        const next = allowedOverview(migrateLegacyStatKeys(parsed));
        setSelectedOverviewStats(
          parsed.length === 0
            ? []
            : next.length > 0
              ? next
              : monthlyStats,
        );
      } else {
        setSelectedOverviewStats(monthlyStats);
        sessionStorage.setItem(
          ZERO_OVERVIEW_STATS_KEY,
          JSON.stringify(monthlyStats),
        );
      }
    } catch {
      setSelectedOverviewStats(monthlyStats);
    }

    if (selectedOrganization?.id) {
      fetchDashboardStatistics(selectedOrganization.id, orgType, monthToUse);
    }
  }, [selectedOrganization?.id, selectedOrganization?.organization_type]);

  useEffect(() => {
    clearAggregatedDashboard();
  }, [selectedOrganization?.id, clearAggregatedDashboard]);

  useEffect(() => {
    if (
      dashboardViewTab !== 1 ||
      !selectedOrganization?.id ||
      !selectedOrganization?.organization_type
    ) {
      return;
    }
    fetchAggregatedDashboardStatistics(
      selectedOrganization.id,
      selectedOrganization.organization_type
    );
  }, [
    dashboardViewTab,
    selectedOrganization?.id,
    selectedOrganization?.organization_type,
    fetchAggregatedDashboardStatistics,
  ]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    statId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedStat(statId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedStat(null);
  };

  const handleChartTypeChange = (type: "bar" | "line" | "area" | "pie") => {
    if (selectedStat) {
      setChartTypes((prev) => ({ ...prev, [selectedStat]: type }));
      handleMenuClose();
    }
  };

  const handleCloseKPIDialog = () => {
    if (helpKpiGuide) {
      finishKpiGuide();
      return;
    }
    setOpenKPIDialog(false);
    // Same as Customize/Compare: closing the dialog returns to the trigger
    // tip — do NOT Later-dismiss the whole onboarding (that hid the flow
    // after logout/re-login when users closed KPI to reach the account menu).
    if (
      showDashboardTour &&
      (tourPhase === "kpiForm" || tourPhase === "kpiList")
    ) {
      setTourPhase("headerActions");
      return;
    }
    clearHelpGuideActive();
  };

  const handleSavePreferences = () => {
    setOpenDialog(false);
    if (activeCustomizeGuide) {
      leaveCustomizeToKeyNumbers();
    }
  };

  const handleMonthChange = (date: string) => {
    setSelectedMonth(date);
    if (selectedOrganization?.id && selectedOrganization?.organization_type) {
      fetchDashboardStatistics(
        selectedOrganization.id,
        selectedOrganization.organization_type,
        date
      );
    }
    if (showDashboardTour && tourPhase === "monthSelect") {
      goNextDashboardTip();
    }
  };

  const queryClient = useQueryClient();
  const fmt = useNumberFormat();
  const { can } = useAuthPermissions();
  const borders = contentBorders(theme);

  if (!selectedOrganization?.id) {
    return (
      <EmptyState
        icon={ZERO_ICONS.dashboard}
        title={t("dashboard.selectOrganization")}
        minHeight={240}
      />
    );
  }

  const orgId = selectedOrganization.id;
  const orgType = selectedOrganization.organization_type;
  const isMonthlyTab = dashboardViewTab === 0;
  const isTruck = orgType === OrganizationType.TRUCK;
  const isRecycler = orgType === OrganizationType.RECYCLER;
  const isSiteOps = isSiteOperationsOrgType(orgType);
  const monthStart = monthStartOf(selectedMonth);
  const monthLabel = formatMonth(monthStart, i18n.language);
  const now = new Date();
  const isCurrentMonth =
    monthStart.getFullYear() === now.getFullYear() && monthStart.getMonth() === now.getMonth();
  const previousMonthLabel = formatMonth(
    new Date(monthStart.getFullYear(), monthStart.getMonth() - 1, 1),
    i18n.language,
  );
  const fetchedAt = isMonthlyTab ? statisticsFetchedAt : aggregatedFetchedAt;
  const refreshing = isMonthlyTab ? isLoading : isLoadingAggregated;

  const handleRefresh = () => {
    if (isMonthlyTab) {
      fetchDashboardStatistics(orgId, orgType, selectedMonth);
    } else {
      fetchAggregatedDashboardStatistics(orgId, orgType);
    }
    void queryClient.invalidateQueries({ queryKey: ["zero"] });
  };

  const displayStatistics = isMonthlyTab ? statistics : aggregatedStatistics;

  const renderDashboardHeader = () => (
    <PageHeader
      icon={<ZERO_ICONS.dashboard />}
      title={t("dashboard.title")}
      subtitle={`${selectedOrganization.name} · ${t(`orgTypes.${orgType}`, { defaultValue: orgType })}`}
      actions={
        <>
          <FreshnessLabel fetchedAt={fetchedAt} refreshing={refreshing} onRefresh={handleRefresh} />
          <SetupCoachmark
            open={
              (showDashboardTour && tourPhase === "headerActions") ||
              (helpKpiGuide && kpiTourPhase === "button")
            }
            title={
              showDashboardTour && tourPhase === "headerActions"
                ? t("selfSetup.tour.headerActionsTitle")
                : tHelp("knowledgeBase.kpiGuideButtonTitle")
            }
            subtitle={
              showDashboardTour && tourPhase === "headerActions"
                ? t("selfSetup.tour.headerActionsSubtitle")
                : tHelp("knowledgeBase.kpiGuideButtonSubtitle")
            }
            onDismiss={
              showDashboardTour && tourPhase === "headerActions"
                ? softDismissDashboardTour
                : finishKpiGuide
            }
            onNext={
              showDashboardTour && tourPhase === "headerActions"
                ? goNextDashboardTip
                : undefined
            }
            nextLabel={t("selfSetup.tourNext")}
          >
            <Button
              id="help-kpi-limits-button"
              variant="outlined"
              color="inherit"
              startIcon={<ZERO_ICONS.targets />}
              onClick={() => {
                setOpenKPIDialog(true);
                if (helpKpiGuide) setKpiTourPhase("form");
                if (showDashboardTour && tourPhase === "headerActions") {
                  setTourPhase("kpiForm");
                }
              }}
            >
              {t("dashboard.kpiLimits")}
            </Button>
          </SetupCoachmark>
          <SetupCoachmark
            open={showCustomizeButtonTip}
            title={
              helpCustomizeGuide
                ? tHelp("knowledgeBase.customizeGuideTitle")
                : t("selfSetup.tour.dashboardTitle")
            }
            subtitle={
              helpCustomizeGuide
                ? tHelp("knowledgeBase.customizeGuideSubtitle")
                : t("selfSetup.tour.dashboardSubtitle")
            }
            onDismiss={softDismissDashboardTour}
          >
            <Button
              id="self-setup-dashboard-customize"
              variant="contained"
              startIcon={<ZERO_ICONS.customize />}
              onClick={() => {
                handleOpenDialog();
              }}
              aria-label={t("dashboard.customize")}
              sx={{ whiteSpace: "nowrap" }}
            >
              {t("dashboard.customize")}
            </Button>
          </SetupCoachmark>
        </>
      }
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          columnGap: 2,
          rowGap: 1,
          borderBottom: `1px solid ${borders.default}`,
        }}
      >
        <SetupCoachmark
          open={showDashboardTour && tourPhase === "monthlyTab"}
          title={t("selfSetup.tour.monthlyTabTitle")}
          subtitle={t("selfSetup.tour.monthlyTabSubtitle")}
          onDismiss={softDismissDashboardTour}
          onNext={goNextDashboardTip}
          nextLabel={t("selfSetup.tourNext")}
        >
          <Tabs
            id="self-setup-dashboard-monthly-tab"
            value={dashboardViewTab}
            onChange={(_, v) => setDashboardViewTab(v)}
            variant="scrollable"
            scrollButtons={false}
            sx={{ minHeight: 0, mb: "-1px", "& .MuiTab-root": { minHeight: 44 } }}
          >
            <Tab label={t("dashboard.tabs.monthly")} />
            <Tab label={t("dashboard.tabs.organizationOverview")} />
          </Tabs>
        </SetupCoachmark>
        {isMonthlyTab ? (
          <SetupCoachmark
            open={showDashboardTour && tourPhase === "monthSelect"}
            title={t("selfSetup.tour.monthSelectTitle")}
            subtitle={t("selfSetup.tour.monthSelectSubtitle")}
            onDismiss={softDismissDashboardTour}
            onNext={goNextDashboardTip}
            nextLabel={t("selfSetup.tourNext")}
          >
            <Select
              id="self-setup-dashboard-month-select"
              size="small"
              value={selectedMonth}
              onChange={(e) => handleMonthChange(e.target.value)}
              aria-label={t("dashboard.month")}
              startAdornment={<ZERO_ICONS.period sx={{ fontSize: 18, color: "text.secondary", marginInlineEnd: 1 }} />}
              sx={{ minWidth: 200, mb: 1 }}
            >
              {availableMonths.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {formatMonth(month.date, i18n.language)}
                </MenuItem>
              ))}
            </Select>
          </SetupCoachmark>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("dashboard.lifetimeHint")}
          </Typography>
        )}
      </Box>
    </PageHeader>
  );

  const renderDashboardSkeleton = () => (
    <Box aria-busy="true" sx={{ display: "grid", gap: 3 }}>
      <Skeleton variant="rounded" height={168} />
      <Skeleton variant="rounded" height={112} />
      <Skeleton variant="rounded" height={420} />
    </Box>
  );

  const suspended = showDashboardTour || helpCustomizeGuide || openDialog;

  if (isMonthlyTab && isLoading && !statistics && !suspended) {
    return (
      <Box>
        {renderDashboardHeader()}
        {renderDashboardSkeleton()}
      </Box>
    );
  }

  if (isMonthlyTab && error && !suspended) {
    return (
      <Box>
        {renderDashboardHeader()}
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              {t("common.retry")}
            </Button>
          }
        >
          {t("dashboard.errorLoading", { error })}
        </Alert>
      </Box>
    );
  }

  if (isMonthlyTab && !statistics && !suspended) {
    return (
      <Box>
        {renderDashboardHeader()}
        <EmptyState
          icon={ZERO_ICONS.collections}
          title={t("dashboard.emptyTitle")}
          description={t("dashboard.emptyDescription")}
          minHeight={320}
          action={
            <Button variant="outlined" color="inherit" onClick={() => navigate("/organizations")}>
              {t("dashboard.goToOrganizations")}
            </Button>
          }
        />
      </Box>
    );
  }

  if (!isMonthlyTab && isLoadingAggregated && !aggregatedStatistics && !suspended) {
    return (
      <Box>
        {renderDashboardHeader()}
        {renderDashboardSkeleton()}
      </Box>
    );
  }

  if (!isMonthlyTab && aggregatedError && !aggregatedStatistics && !suspended) {
    return (
      <Box>
        {renderDashboardHeader()}
        <Alert severity="error">{aggregatedError}</Alert>
      </Box>
    );
  }

  if (!displayStatistics && !suspended) {
    return (
      <Box>
        {renderDashboardHeader()}
        <EmptyState icon={ZERO_ICONS.insights} title={t("dashboard.noData")} minHeight={240} />
      </Box>
    );
  }

  const stats = (displayStatistics ?? {}) as DashboardStatistics;
  const statsRecord = stats as unknown as Record<string, unknown>;
  const num = (key: string): number | null => {
    const v = statsRecord[key];
    return typeof v === "number" && Number.isFinite(v) ? v : null;
  };

  const tabStats = isMonthlyTab ? selectedStats : selectedOverviewStats;
  const tabExtras = isMonthlyTab ? monthlyExtras : overviewExtras;
  const extraIdsForTab = getDashboardExtrasForOrgTab(orgType, isMonthlyTab ? "monthly" : "overview");
  const statIdsForTab = getStatsForOrgType(orgType);
  const extraLabel = (id: DashboardExtraWidgetId) =>
    id === DASHBOARD_EXTRA_MAP
      ? isTruck || isRecycler
        ? t("dashboard.extras.truckMap")
        : t("dashboard.extras.containerMap")
      : t("dashboard.extras.kpiProgress");
  const customizeItems = [
    ...extraIdsForTab.map((id) => ({ id, label: extraLabel(id) })),
    ...statIdsForTab.map((id) => ({ id, label: getDisplayTitle(id) })),
  ];
  const customizeSelected = [...tabExtras, ...tabStats];

  // ---- figures ------------------------------------------------------------------------------
  /** Month-over-month only for a completed month (month-to-date vs a full month misleads). */
  const showDeltas = isMonthlyTab && !isCurrentMonth;
  const deltaFor = (stat: string): KpiDelta | undefined => {
    if (!showDeltas) return undefined;
    const current = num(stat);
    const previous = num(`previous_month_${stat}`);
    if (current === null || previous === null || previous === 0) return undefined;
    const percent = getStatValueKind(stat) === "percent";
    return {
      value: percent ? current - previous : ((current - previous) / Math.abs(previous)) * 100,
      kind: percent ? "points" : "relative",
      polarity: operationalStatPolarity(stat),
      comparisonLabel: t("dashboard.delta.vsMonth", { month: previousMonthLabel }),
    };
  };

  const leadShown = tabStats.includes(StatisticType.TOTAL_ORDERS) && num("total_orders") !== null;
  // Completion by mass sits under the order count when both are shown.
  const leadCompletionByMass =
    leadShown &&
    tabStats.includes(StatisticType.WASTE_DIVERSION_RATE) &&
    num(StatisticType.WASTE_DIVERSION_RATE) !== null;
  const leadCovers = new Set<string>(
    leadShown
      ? [
          StatisticType.TOTAL_ORDERS,
          StatisticType.COMPLETION_RATE,
          StatisticType.CANCELLATION_RATE,
          ...(leadCompletionByMass ? [StatisticType.WASTE_DIVERSION_RATE] : []),
        ]
      : [],
  );
  const stripStats = tabStats.filter((stat) => {
    if (leadCovers.has(stat)) return false;
    if (stat === StatisticType.TRIPS_TODAY_COUNT && !(isMonthlyTab && isCurrentMonth)) return false;
    return num(stat) !== null;
  });

  const figure = (stat: string, value: number | null) => {
    const m = fmt.metric(getStatValueKind(stat), value, { scaleMass: true });
    return { text: m.text, unit: m.unit };
  };

  const totalOrdersCount = num("total_orders") ?? 0;
  const statusSlices: OrderStatusSlice[] = [
    {
      key: "completed",
      label: isRecycler ? t("dashboard.lead.received") : t("dashboard.lead.completed"),
      value: num("completed_orders") ?? 0,
      tone: "complete",
    },
    { key: "in_transit", label: t("dashboard.lead.inTransit"), value: num("in_transit_orders") ?? 0, tone: "active" },
    { key: "pending", label: t("dashboard.lead.pending"), value: num("pending_orders") ?? 0, tone: "pending" },
    { key: "cancelled", label: t("dashboard.lead.cancelled"), value: num("cancelled_orders") ?? 0, tone: "failed" },
  ];
  // Statuses the summary has no counter for (e.g. rejected) — shown, not hidden.
  const otherOrders = totalOrdersCount - statusSlices.reduce((sum, s) => sum + s.value, 0);
  if (otherOrders > 0) {
    statusSlices.push({ key: "other", label: t("dashboard.lead.otherStatus"), value: otherOrders, tone: "offline" });
  }

  const leadLine = leadShown ? (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(220px, 0.9fr) minmax(0, 2fr)" },
        bgcolor: "background.paper",
        border: `1px solid ${borders.default}`,
        borderRadius: `${CONTENT_SHAPE.md}px`,
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: { xs: 2, md: 3 }, display: "flex", flexDirection: "column", justifyContent: "center", gap: 2.5 }}>
        <KpiFigure
          icon={ZERO_ICONS.collections}
          label={isRecycler ? t("dashboard.lead.incoming") : t("dashboard.lead.collections")}
          value={fmt.number(num("total_orders"))}
          size="hero"
          delta={deltaFor("total_orders")}
        />
        {leadCompletionByMass ? (
          <Box sx={{ pt: 2.5, borderTop: `1px solid ${borders.subtle}` }}>
            <KpiFigure
              icon={STAT_ICONS[StatisticType.WASTE_DIVERSION_RATE] ?? ZERO_ICONS.insights}
              label={getDisplayTitle(StatisticType.WASTE_DIVERSION_RATE)}
              value={figure(StatisticType.WASTE_DIVERSION_RATE, num(StatisticType.WASTE_DIVERSION_RATE)).text}
              unit={figure(StatisticType.WASTE_DIVERSION_RATE, num(StatisticType.WASTE_DIVERSION_RATE)).unit || undefined}
              size="md"
              delta={deltaFor(StatisticType.WASTE_DIVERSION_RATE)}
            />
          </Box>
        ) : null}
      </Box>
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          borderTop: { xs: `1px solid ${borders.subtle}`, md: 0 },
          borderInlineStart: { md: `1px solid ${borders.subtle}` },
          minWidth: 0,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1.5 }}>
          {t("dashboard.lead.byStatus")}
        </Typography>
        <OrderStatusDonut
          slices={statusSlices}
          total={totalOrdersCount}
          ariaLabel={t("dashboard.lead.byStatus")}
        />
      </Box>
    </Box>
  ) : null;

  const strip =
    stripStats.length > 0 ? (
      <MetricStrip columns={{ xs: 2, sm: 3, lg: Math.min(Math.max(stripStats.length, 3), 5) }}>
        {stripStats.map((stat) => {
          const f = figure(stat, num(stat));
          return (
            <KpiFigure
              key={stat}
              icon={STAT_ICONS[stat] ?? ZERO_ICONS.insights}
              label={getDisplayTitle(stat)}
              value={f.text}
              unit={f.unit || undefined}
              size="md"
              delta={deltaFor(stat)}
            />
          );
        })}
      </MetricStrip>
    ) : null;

  // KPI targets open the dashboard, in view right after a limit is added.
  const kpiTargets =
    stats && hasTrackedKPIs && tabExtras.includes(DASHBOARD_EXTRA_KPI) ? (
      <Box component="section" sx={{ mb: { xs: 4, md: 5 } }}>
        <SectionHeader title={t("dashboard.targets.title")} icon={ZERO_ICONS.targets} />
        <KpiTargetsTable limits={limits} source={stats} orgType={orgType} />
      </Box>
    ) : null;

  const keyNumbersVisible = Boolean(leadLine || strip);

  // ---- operations intelligence -----------------------------------------------------------------
  const containers = stats.containers ?? [];
  const mapOn = tabExtras.includes(DASHBOARD_EXTRA_MAP);
  const canRuns = can(orgScopedPermission(orgId, "route_run", "read"));
  const canTrucks = can(orgScopedPermission(orgId, "truck", "list"));
  const canBranchMetrics =
    orgType === OrganizationType.WASTE_OUTLET && can(orgScopedPermission(orgId, "esg_report", "read"));

  const siteSurface =
    mapOn && isSiteOps && isMonthlyTab ? (
      <SiteCommandSurface
        orgId={orgId}
        containers={containers}
        monthValue={selectedMonth}
        canBranchMetrics={canBranchMetrics}
      />
    ) : null;
  const fleetSurface =
    mapOn && (isTruck || isRecycler) && (canRuns || canTrucks) ? (
      <FleetCommandSurface orgId={orgId} canRuns={canRuns} canTrucks={canTrucks} />
    ) : null;

  const totalOrders = num("total_orders");
  const flowStages: FlowStage[] = [];
  if (totalOrders !== null && totalOrders > 0) {
    if (isSiteOps && (num("total_containers") ?? 0) > 0) {
      flowStages.push({
        key: "sources",
        icon: ZERO_ICONS.container,
        label: t("dashboard.flow.sources"),
        value: fmt.number(num("total_containers")),
        caption: t("dashboard.flow.containersRegistered"),
      });
    }
    flowStages.push({
      key: "requested",
      icon: ZERO_ICONS.collections,
      label: t("dashboard.flow.requested"),
      value: fmt.number(totalOrders),
      caption: [
        num("total_waste_amount") !== null ? fmt.withUnit("mass_kg", num("total_waste_amount"), { scaleMass: true }) : null,
        t("dashboard.flow.pendingCancelled", {
          pending: fmt.number(num("pending_orders") ?? 0),
          cancelled: fmt.number(num("cancelled_orders") ?? 0),
        }),
      ]
        .filter(Boolean)
        .join(" · "),
    });
    flowStages.push({
      key: "transit",
      icon: ZERO_ICONS.inTransit,
      label: t("dashboard.flow.inTransit"),
      value: fmt.number(num("in_transit_orders") ?? 0),
    });
    const collectedKg = isRecycler ? num("sustainable_supply") : num("completed_waste_amount");
    flowStages.push({
      key: "collected",
      icon: ZERO_ICONS.completed,
      label: isRecycler ? t("dashboard.flow.received") : t("dashboard.flow.collected"),
      value: fmt.number(num("completed_orders") ?? 0),
      caption: collectedKg !== null ? fmt.withUnit("mass_kg", collectedKg, { scaleMass: true }) : undefined,
      emphasis: orgType !== OrganizationType.WASTE_OUTLET,
    });
    if (orgType === OrganizationType.WASTE_OUTLET && num("total_revenue") !== null) {
      flowStages.push({
        key: "revenue",
        icon: ZERO_ICONS.revenue,
        label: t("dashboard.flow.revenue"),
        value: fmt.metric("sar", num("total_revenue")).text,
        unit: t("units.sar"),
        emphasis: true,
      });
    }
  }

  const breakdown = (stats.waste_type_material_breakdown ?? []).filter((r) => r.total_kg > 0);
  const daily = stats.incoming_waste_breakdown ?? [];
  const showMaterials = tabStats.includes(StatisticType.WASTE_TYPE_BREAKDOWN) && breakdown.length > 0;
  const showDaily =
    tabStats.includes(StatisticType.INCOMING_WASTE_BREAKDOWN) && daily.some((d) => (d.amount ?? 0) > 0);
  const dailyType: DailyChartType = (chartTypes[StatisticType.INCOMING_WASTE_BREAKDOWN] as DailyChartType) ?? "bar";
  // Per-material days come from the month's pickup orders, which only match the
  // dashboard's daily totals for the organisation's own orders (outlets, charities).
  const canSplitDaily =
    isSiteOps && isMonthlyTab && can(orgScopedPermission(orgId, "pickup_order", "list"));
  const dailyByMaterial = canSplitDaily && dailyView === "material";
  const dailyTotalKg = daily.reduce((sum, d) => sum + (Number.isFinite(d.amount) ? d.amount : 0), 0);
  const showContainerHealth = isSiteOps && isMonthlyTab && containers.length > 0 && tabStats.includes(StatisticType.FULL_PERCENTAGE);

  const operationsVisible = Boolean(
    siteSurface || fleetSurface || flowStages.length > 1 || showMaterials || showDaily || showContainerHealth,
  );

  const panelSx = {
    bgcolor: "background.paper",
    border: `1px solid ${borders.default}`,
    borderRadius: `${CONTENT_SHAPE.md}px`,
    p: { xs: 2, md: 2.5 },
    minWidth: 0,
  } as const;

  return (
    <Box sx={{ animation: "zero-fade-in 240ms ease-out" }}>
      {renderDashboardHeader()}

      {kpiTargets}

      {/* Operations summary — what happened this period */}
      {keyNumbersVisible && (
        <SetupCoachmark
          open={showDashboardTour && tourPhase === "keyNumbers"}
          title={t("selfSetup.tour.keyNumbersTitle")}
          subtitle={t("selfSetup.tour.keyNumbersSubtitle")}
          onDismiss={softDismissDashboardTour}
          onNext={goNextDashboardTip}
          nextLabel={t("selfSetup.tourNext")}
          fullWidth
        >
          <Box id="self-setup-dashboard-key-numbers" component="section" sx={{ mb: { xs: 4, md: 5 }, width: "100%" }}>
            <SectionHeader
              title={
                isMonthlyTab
                  ? isCurrentMonth
                    ? t("dashboard.lead.titleMonthToDate", { period: monthLabel })
                    : t("dashboard.lead.title", { period: monthLabel })
                  : t("dashboard.lead.titleLifetime")
              }
              icon={ZERO_ICONS.collections}
            />
            <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 2 }}>
              {leadLine}
              {strip}
            </Box>
          </Box>
        </SetupCoachmark>
      )}

      {/* What it is (materials), goals, then where it happens and what moves */}
      {operationsVisible && (
        <SetupCoachmark
          open={showDashboardTour && tourPhase === "charts"}
          title={t("selfSetup.tour.chartsTitle")}
          subtitle={t("selfSetup.tour.chartsSubtitle")}
          onDismiss={softDismissDashboardTour}
          onNext={goNextDashboardTip}
          nextLabel={t("selfSetup.tourNext")}
          fullWidth
        >
          <Box id="self-setup-dashboard-charts" sx={{ width: "100%", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: { xs: 4, md: 5 } }}>
            {showMaterials || showDaily ? (
              <Box component="section">
                <SectionHeader
                  title={t("dashboard.materials.title")}
                  icon={ZERO_ICONS.materials}
                  description={t("dashboard.materials.description")}
                />
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: {
                      xs: "minmax(0, 1fr)",
                      lg: showMaterials && showDaily ? "minmax(0, 7fr) minmax(0, 5fr)" : "minmax(0, 1fr)",
                    },
                    // Side by side, both cards take the taller one's height.
                    alignItems: "stretch",
                  }}
                >
                  {showMaterials ? (
                    <Box sx={{ ...panelSx, display: "flex", flexDirection: "column" }}>
                      <SectionHeader
                        level="minor"
                        component="h3"
                        title={getDisplayTitle(StatisticType.WASTE_TYPE_BREAKDOWN)}
                        icon={ZERO_ICONS.materials}
                      />
                      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0 }}>
                        <MaterialList breakdown={breakdown} ariaLabel={getDisplayTitle(StatisticType.WASTE_TYPE_BREAKDOWN)} showChart />
                      </Box>
                    </Box>
                  ) : null}
                  {showDaily ? (
                    <Box sx={panelSx}>
                      <SectionHeader
                        level="minor"
                        component="h3"
                        title={getDisplayTitle(StatisticType.INCOMING_WASTE_BREAKDOWN)}
                        icon={ZERO_ICONS.daily}
                        description={t("dashboard.daily.description")}
                        actions={
                          <>
                            {canSplitDaily ? (
                              <ToggleButtonGroup
                                exclusive
                                size="small"
                                color="primary"
                                value={dailyView}
                                onChange={(_, next: "total" | "material" | null) => {
                                  if (next) setDailyView(next);
                                }}
                                aria-label={t("dashboard.daily.view.label")}
                                sx={{
                                  "& .MuiToggleButton-root": {
                                    px: 1.25,
                                    py: 0.25,
                                    textTransform: "none",
                                    fontWeight: 500,
                                    fontSize: "0.8125rem",
                                    lineHeight: 1.6,
                                  },
                                }}
                              >
                                <ToggleButton value="total">{t("dashboard.daily.view.total")}</ToggleButton>
                                <ToggleButton value="material">{t("dashboard.daily.view.byMaterial")}</ToggleButton>
                              </ToggleButtonGroup>
                            ) : null}
                            <Tooltip title={t("dashboard.chartTypes.title")}>
                              <IconButton
                                size="small"
                                aria-label={t("dashboard.chartTypes.title")}
                                onClick={(e) => handleMenuOpen(e, StatisticType.INCOMING_WASTE_BREAKDOWN)}
                                sx={{ color: "text.secondary" }}
                              >
                                <MoreHorizIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        }
                      />
                      {dailyByMaterial ? (
                        <DailyMaterialSplit
                          orgId={orgId}
                          monthStart={monthStart}
                          syncKey={statisticsFetchedAt}
                          expectedTotalKg={dailyTotalKg}
                          type={dailyType}
                          ariaLabel={getDisplayTitle(StatisticType.INCOMING_WASTE_BREAKDOWN)}
                        />
                      ) : (
                        <DailyRequestsChart
                          rows={daily}
                          monthStart={isMonthlyTab ? monthStart : null}
                          type={dailyType}
                          ariaLabel={getDisplayTitle(StatisticType.INCOMING_WASTE_BREAKDOWN)}
                        />
                      )}
                    </Box>
                  ) : null}
                </Box>
              </Box>
            ) : null}

            {siteSurface ? (
              <Box component="section">
                <SectionHeader
                  title={t("dashboard.site.title")}
                  icon={ZERO_ICONS.map}
                  description={t("dashboard.site.description")}
                />
                {siteSurface}
              </Box>
            ) : null}

            {fleetSurface}

            {flowStages.length > 1 ? (
              <Box component="section">
                <SectionHeader
                  title={
                    isMonthlyTab
                      ? t("dashboard.flow.title", { period: monthLabel })
                      : t("dashboard.flow.titleLifetime")
                  }
                  icon={ZERO_ICONS.flow}
                  description={t("dashboard.flow.description")}
                />
                <CollectionFlow stages={flowStages} ariaLabel={t("dashboard.flow.titleLifetime")} />
              </Box>
            ) : null}

            {showContainerHealth ? (
              <Box component="section">
                <SectionHeader
                  title={t("dashboard.containers.title")}
                  icon={ZERO_ICONS.container}
                  description={t("dashboard.containers.description")}
                />
                <ContainerHealth containers={containers} />
              </Box>
            ) : null}
          </Box>
        </SetupCoachmark>
      )}

      {!keyNumbersVisible && !kpiTargets && !operationsVisible ? (
        <EmptyState
          icon={ZERO_ICONS.customize}
          title={t("dashboard.nothingSelected")}
          description={t("dashboard.nothingSelectedHint")}
          minHeight={240}
          action={
            <Button variant="outlined" color="inherit" onClick={handleOpenDialog}>
              {t("dashboard.customize")}
            </Button>
          }
        />
      ) : null}

      {/* Chart type menu (daily requests) */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleChartTypeChange("bar")} selected={dailyType === "bar"}>
          <ListItemIcon>
            <BarChartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("dashboard.chartTypes.bar")}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleChartTypeChange("line")} selected={dailyType === "line"}>
          <ListItemIcon>
            <LineChartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("dashboard.chartTypes.line")}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleChartTypeChange("area")} selected={dailyType === "area"}>
          <ListItemIcon>
            <AreaChartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("dashboard.chartTypes.area")}</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="customize-dashboard-title"
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            px: { xs: 2.5, sm: 3 },
            pt: { xs: 2.5, sm: 3 },
            pb: 2.5,
            borderBottom: `1px solid ${borders.default}`,
          }}
        >
          <Box
            aria-hidden
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              borderRadius: `${CONTENT_SHAPE.sm}px`,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: "primary.main",
            }}
          >
            <ZERO_ICONS.customize sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography id="customize-dashboard-title" component="h2" variant="h6" sx={{ m: 0 }}>
              {isMonthlyTab
                ? t("dashboard.customizeMonthly")
                : t("dashboard.customizeOverview")}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
              {isMonthlyTab
                ? t("dashboard.customizeMonthlyHint")
                : t("dashboard.customizeOverviewHint")}
            </Typography>
          </Box>
          <IconButton
            aria-label={t("common.close")}
            onClick={handleCloseDialog}
            sx={{ mt: -0.5, mx: -0.5, color: "text.secondary" }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        <DialogContent
          dividers={false}
          sx={{
            maxHeight: "55vh",
            px: { xs: 2.5, sm: 3 },
            pt: 2.5,
            pb: 1,
          }}
        >
          <SetupCoachmark
            open={
              showCustomizeDialogTip &&
              activeCustomizeDialogPhase === "search"
            }
            title={t("selfSetup.dashboardGuide.step2")}
            subtitle={t("selfSetup.dashboardGuide.intro")}
            onDismiss={softDismissDashboardTour}
            onNext={advanceCustomizeDialogTour}
            nextLabel={t("selfSetup.tourNext")}
            fullWidth
          >
            <Box id="help-customize-search" sx={{ mb: 2, width: "100%" }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t("dashboard.searchMetrics")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ marginInlineEnd: 1, opacity: 0.6, display: "inline-flex" }}>
                      <SearchIcon fontSize="small" />
                    </Box>
                  ),
                }}
              />
            </Box>
          </SetupCoachmark>

          <SetupCoachmark
            open={
              showCustomizeDialogTip &&
              activeCustomizeDialogPhase === "metrics"
            }
            title={t("selfSetup.dashboardGuide.step3")}
            subtitle={t("selfSetup.tour.dashboardSubtitle")}
            onDismiss={softDismissDashboardTour}
            onNext={advanceCustomizeDialogTour}
            nextLabel={t("selfSetup.tourNext")}
            fullWidth
          >
            <Box id="help-customize-metrics" sx={{ width: "100%" }}>
              <DashboardCustomizeWidgetList
                items={customizeItems}
                selectedIds={customizeSelected}
                searchQuery={searchQuery}
                selectAllLabel={t("dashboard.selectAllMetrics")}
                onChange={(next) => {
                  const extras = extraIdsForTab.filter((id) => next.includes(id));
                  const stats = statIdsForTab.filter((id) => next.includes(id));
                  if (isMonthlyTab) {
                    setMonthlyExtras(extras);
                    setSelectedStats(stats);
                    sessionStorage.setItem(
                      ZERO_MONTHLY_EXTRAS_KEY,
                      JSON.stringify(extras),
                    );
                  } else {
                    setOverviewExtras(extras);
                    setSelectedOverviewStats(stats);
                    sessionStorage.setItem(
                      ZERO_OVERVIEW_EXTRAS_KEY,
                      JSON.stringify(extras),
                    );
                    sessionStorage.setItem(
                      ZERO_OVERVIEW_STATS_KEY,
                      JSON.stringify(stats),
                    );
                  }
                }}
              />
            </Box>
          </SetupCoachmark>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: 2,
            gap: 1,
            borderTop: `1px solid ${borders.default}`,
          }}
        >
          <Button onClick={handleCloseDialog} variant="outlined" color="inherit">
            {t("common.cancel")}
          </Button>
          <SetupCoachmark
            open={
              showCustomizeDialogTip &&
              activeCustomizeDialogPhase === "save"
            }
            title={t("selfSetup.dashboardGuide.step4")}
            subtitle={t("selfSetup.dashboardGuide.dialogTipBody")}
            onDismiss={softDismissDashboardTour}
            onNext={advanceCustomizeDialogTour}
            nextLabel={t("selfSetup.tourDone")}
          >
            <Button
              id="help-customize-save"
              onClick={handleSavePreferences}
              variant="contained"
            >
              {t("dashboard.saveChanges")}
            </Button>
          </SetupCoachmark>
        </DialogActions>
      </Dialog>

      <KPILimitsDialog
        open={openKPIDialog}
        onClose={handleCloseKPIDialog}
        orgType={orgType}
        options={getStatsForOrgType(orgType)}
        helpGuide={helpKpiGuide && kpiTourPhase === "form"}
        onHelpGuideDone={() => {
          setHelpKpiGuide(false);
          setKpiTourPhase("button");
          clearHelpGuideActive();
        }}
        tourTip={
          showDashboardTour &&
          (tourPhase === "kpiForm" || tourPhase === "kpiList")
            ? tourPhase
            : null
        }
        onTourNext={goNextDashboardTip}
        onTourDismiss={softDismissDashboardTour}
        tourFormTitle={t("selfSetup.tour.kpiFormTitle")}
        tourFormSubtitle={t("selfSetup.tour.kpiFormSubtitle")}
        tourListTitle={t("selfSetup.tour.kpiListTitle")}
        tourListSubtitle={t("selfSetup.tour.kpiListSubtitle")}
        tourNextLabel={t("selfSetup.tourNext")}
      />
    </Box>
  );
};

const Dashboard: React.FC = () => {
  const isPilot = useAuthStore((state) => state.tokenPayload?.is_pilot === true);
  return isPilot ? <PilotDashboard /> : <StandardDashboard />;
};

export default Dashboard;
